import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from './App'
// Side-effect import — runs the react-i18next init before the app renders.
import './i18n'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      // networkMode 'always': never gate fetches on React Query's online
      // detection. In the default 'online' mode, a flaky navigator.onLine /
      // connectivity signal PAUSES queries — they never fetch (endless spinner),
      // a manual refetch stays paused (the "Reintentar" button does nothing),
      // and only a tab-focus reconnect resumes them. That exactly matched the
      // "panorama won't load until I switch tabs, and Reintentar does nothing"
      // report. 'always' just fetches.
      networkMode: 'always',
      // Recover a transient first-request failure (e.g. a token race right after
      // login) quickly + invisibly. Fast fixed-ish backoff, capped.
      //
      // 403 is excluded because it is never transient: a permission answer does
      // not change between attempts. Retrying it re-entered the MFA-bounce
      // interceptor up to three times (three `window.location.assign` calls) and
      // — worse — delayed the bouncing query's error by ~1.2s while any
      // `retry: false` query on the same page painted its error state first.
      // That is what made the manage pages error "sometimes" (checkpoint-2 F1).
      retry: (attempt, error) =>
        (error as { response?: { status?: number } } | undefined)?.response?.status !== 403 &&
        attempt < 2,
      retryDelay: (attempt) => Math.min(400 * 2 ** attempt, 2000),
    },
    mutations: { networkMode: 'always' },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root not found in index.html')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
